export interface DetailedRoiInput {
  annualAudits: number;
  auditPrepHours: number;
  auditStaffSalary: number;
  monthlyClaims: number;
  denialRate: number;
  deniedClaimValue: number;
  appealSuccessRate: number;
}

export interface DetailedRoiResult {
  annualAuditCost: {
    withoutFownd: number;
    withFownd: number;
  };
  annualRevenueLost: {
    withoutFownd: number;
    withFownd: number;
  };
  recoveredRevenue: {
    withoutFownd: number;
    withFownd: number;
  };
  netSavings: number;
  staffCostSavings: number;
  denialRecovery: number;
  roiPercentage: number;
}

// Constants for Fownd's performance metrics
const FOWND_CONSTANTS = {
  AUDIT_HOURS: 5,
  DENIAL_RATE: 0.02, // 2%
  APPEAL_SUCCESS_RATE: 0.5, // 50%
  MONTHS_PER_YEAR: 12,
} as const;

export const calculateDetailedRoi = (
  input: DetailedRoiInput
): DetailedRoiResult => {
  const {
    annualAudits,
    auditPrepHours,
    auditStaffSalary,
    monthlyClaims,
    denialRate,
    deniedClaimValue,
    appealSuccessRate,
  } = input;

  // 1. Annual Cost of Audit
  const annualAuditCostWithoutFownd =
    annualAudits * auditPrepHours * auditStaffSalary;
  const annualAuditCostWithFownd =
    annualAudits * FOWND_CONSTANTS.AUDIT_HOURS * auditStaffSalary;

  // 2. Annual Revenue Lost from Denials
  const annualClaims = monthlyClaims * FOWND_CONSTANTS.MONTHS_PER_YEAR;
  const annualRevenueLostWithoutFownd =
    annualClaims * (denialRate / 100) * deniedClaimValue;
  const annualRevenueLostWithFownd =
    annualClaims * FOWND_CONSTANTS.DENIAL_RATE * deniedClaimValue;

  // 3. Recovered Revenue from Appeals
  const recoveredRevenueWithoutFownd =
    annualRevenueLostWithoutFownd * (appealSuccessRate / 100);
  const recoveredRevenueWithFownd =
    annualRevenueLostWithFownd * FOWND_CONSTANTS.APPEAL_SUCCESS_RATE;

  // 4. Net Savings
  const staffCostSavings =
    annualAuditCostWithoutFownd - annualAuditCostWithFownd;
  const denialRecovery =
    annualRevenueLostWithoutFownd -
    annualRevenueLostWithFownd +
    (recoveredRevenueWithFownd - recoveredRevenueWithoutFownd);
  const netSavings = staffCostSavings + denialRecovery;

  // 5. ROI Percentage
  const roiPercentage = (netSavings / annualAuditCostWithFownd) * 100;

  return {
    annualAuditCost: {
      withoutFownd: annualAuditCostWithoutFownd,
      withFownd: annualAuditCostWithFownd,
    },
    annualRevenueLost: {
      withoutFownd: annualRevenueLostWithoutFownd,
      withFownd: annualRevenueLostWithFownd,
    },
    recoveredRevenue: {
      withoutFownd: recoveredRevenueWithoutFownd,
      withFownd: recoveredRevenueWithFownd,
    },
    netSavings,
    staffCostSavings,
    denialRecovery,
    roiPercentage,
  };
};
